import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'

export const FORM_FIELD_RENAME_ACTION_ID = 'form.field.rename'
export const FORM_FIELD_CHANGE_TYPE_ACTION_ID = 'form.field.change-type'
export const FORM_FIELD_TOGGLE_REQUIRED_ACTION_ID = 'form.field.toggle-required'
export const FORM_FIELD_DELETE_ACTION_ID = 'form.field.delete'

export const FORM_SECTION_RENAME_ACTION_ID = 'form.section.rename'
export const FORM_SECTION_COLLAPSE_ACTION_ID = 'form.section.collapse'
export const FORM_SECTION_DELETE_ACTION_ID = 'form.section.delete'

export const FORM_SUBMIT_RULES_ACTION_ID = 'form.submit.rules'
export const FORM_SUBMIT_PREVIEW_ACTION_ID = 'form.submit.preview'

export const FORM_CANVAS_ADD_FIELD_ACTION_ID = 'form.canvas.add-field'
export const FORM_CANVAS_ADD_SECTION_ACTION_ID = 'form.canvas.add-section'
export const FORM_CANVAS_PREVIEW_ACTION_ID = 'form.canvas.preview'

export const FORM_RESPONSE_SUBMIT_ACTION_ID = 'form.response.submit'

export type FormOrbitTargetArgs = {
  id?: string
  title?: string
}

const formOrbitTargetArgsSchema: JSONSchemaType<FormOrbitTargetArgs> = {
  type: 'object',
  properties: {
    id: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

const TARGET_REQUIRED_ACTION_IDS = new Set([
  FORM_FIELD_RENAME_ACTION_ID,
  FORM_FIELD_CHANGE_TYPE_ACTION_ID,
  FORM_FIELD_TOGGLE_REQUIRED_ACTION_ID,
  FORM_FIELD_DELETE_ACTION_ID,
  FORM_SECTION_RENAME_ACTION_ID,
  FORM_SECTION_COLLAPSE_ACTION_ID,
  FORM_SECTION_DELETE_ACTION_ID,
])

const makeOrbitFormAction = (
  id: string,
  label: string,
  description: string,
  risk: 'READ_ONLY' | 'LOW' | 'MEDIUM' | 'HIGH',
  permissionAction?: 'forms.design' | 'forms.view',
): ActionDefinition<FormOrbitTargetArgs> => ({
  id,
  label,
  description,
  risk,
  domain: 'form',
  surfaces: ['orbit', 'catalog'],
  argsSchema: formOrbitTargetArgsSchema,
  permission: permissionAction ? (context) => context.canPerform(permissionAction) : undefined,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'

    if (TARGET_REQUIRED_ACTION_IDS.has(id) && !args.id) {
      const message = `Target ID is required for ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    const ops = context.formsOps
    let handler: (() => Promise<void> | void) | undefined

    if (id === FORM_FIELD_RENAME_ACTION_ID) {
      handler = ops?.renameField ? () => ops.renameField!(args.id!) : undefined
    } else if (id === FORM_FIELD_CHANGE_TYPE_ACTION_ID) {
      handler = ops?.changeFieldType ? () => ops.changeFieldType!(args.id!) : undefined
    } else if (id === FORM_FIELD_TOGGLE_REQUIRED_ACTION_ID) {
      handler = ops?.toggleFieldRequired ? () => ops.toggleFieldRequired!(args.id!) : undefined
    } else if (id === FORM_FIELD_DELETE_ACTION_ID) {
      handler = ops?.deleteField ? () => ops.deleteField!(args.id!) : undefined
    } else if (id === FORM_SECTION_RENAME_ACTION_ID) {
      handler = ops?.renameSection ? () => ops.renameSection!(args.id!) : undefined
    } else if (id === FORM_SECTION_COLLAPSE_ACTION_ID) {
      handler = ops?.collapseSection ? () => ops.collapseSection!(args.id!) : undefined
    } else if (id === FORM_SECTION_DELETE_ACTION_ID) {
      handler = ops?.deleteSection ? () => ops.deleteSection!(args.id!) : undefined
    } else if (id === FORM_SUBMIT_RULES_ACTION_ID) {
      handler = ops?.setSubmitRules ? () => ops.setSubmitRules!(args.id) : undefined
    } else if (id === FORM_SUBMIT_PREVIEW_ACTION_ID) {
      handler = ops?.previewSubmit ? () => ops.previewSubmit!(args.id) : undefined
    } else if (id === FORM_CANVAS_ADD_FIELD_ACTION_ID) {
      handler = ops?.addField ? () => ops.addField!() : undefined
    } else if (id === FORM_CANVAS_ADD_SECTION_ACTION_ID) {
      handler = ops?.addSection ? () => ops.addSection!() : undefined
    } else if (id === FORM_CANVAS_PREVIEW_ACTION_ID) {
      handler = ops?.previewCanvas ? () => ops.previewCanvas!() : undefined
    }

    if (!handler) {
      const message = `Operation for ${label} (${id}) is not available`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    try {
      await Promise.resolve(handler())
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to execute ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    context.showToast?.(`${label}${args.title ? ` · ${args.title}` : ''}`, 'info')
    return {
      kind: 'COMPLETED',
      result: { id: args.id, title: args.title },
      message: label,
      correlationId,
    }
  },
})

const formFieldRenameAction = makeOrbitFormAction(
  FORM_FIELD_RENAME_ACTION_ID,
  'Rename',
  'Renames the selected form field.',
  'LOW',
  'forms.design',
)

const formFieldChangeTypeAction = makeOrbitFormAction(
  FORM_FIELD_CHANGE_TYPE_ACTION_ID,
  'Change type',
  'Changes the input type of the selected form field.',
  'LOW',
  'forms.design',
)

const formFieldToggleRequiredAction = makeOrbitFormAction(
  FORM_FIELD_TOGGLE_REQUIRED_ACTION_ID,
  'Required',
  'Toggles the required validation status of the form field.',
  'LOW',
  'forms.design',
)

const formFieldDeleteAction = makeOrbitFormAction(
  FORM_FIELD_DELETE_ACTION_ID,
  'Delete',
  'Deletes the selected form field.',
  'MEDIUM',
  'forms.design',
)

const formSectionRenameAction = makeOrbitFormAction(
  FORM_SECTION_RENAME_ACTION_ID,
  'Rename',
  'Renames the selected form section.',
  'LOW',
  'forms.design',
)

const formSectionCollapseAction = makeOrbitFormAction(
  FORM_SECTION_COLLAPSE_ACTION_ID,
  'Collapse',
  'Toggles collapse or expand state of the form section.',
  'LOW',
  'forms.view',
)

const formSectionDeleteAction = makeOrbitFormAction(
  FORM_SECTION_DELETE_ACTION_ID,
  'Delete',
  'Deletes the selected form section.',
  'MEDIUM',
  'forms.design',
)

const formSubmitRulesAction = makeOrbitFormAction(
  FORM_SUBMIT_RULES_ACTION_ID,
  'Set rules',
  'Configures submission logic and routing rules for the form.',
  'LOW',
  'forms.design',
)

const formSubmitPreviewAction = makeOrbitFormAction(
  FORM_SUBMIT_PREVIEW_ACTION_ID,
  'Preview',
  'Previews the form submit experience and confirmation flow.',
  'READ_ONLY',
  'forms.view',
)

const formCanvasAddFieldAction = makeOrbitFormAction(
  FORM_CANVAS_ADD_FIELD_ACTION_ID,
  'Add field',
  'Appends a new field to the active form.',
  'LOW',
  'forms.design',
)

const formCanvasAddSectionAction = makeOrbitFormAction(
  FORM_CANVAS_ADD_SECTION_ACTION_ID,
  'Add section',
  'Appends a new section to the active form.',
  'LOW',
  'forms.design',
)

const formCanvasPreviewAction = makeOrbitFormAction(
  FORM_CANVAS_PREVIEW_ACTION_ID,
  'Preview',
  'Previews the entire form layout as seen by respondents.',
  'READ_ONLY',
  'forms.view',
)

export type FormResponseSubmitArgs = {
  formId?: string
  responseId?: string
  title?: string
}

const formResponseSubmitArgsSchema: JSONSchemaType<FormResponseSubmitArgs> = {
  type: 'object',
  properties: {
    formId: { type: 'string', nullable: true },
    responseId: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

const formResponseSubmitAction: ActionDefinition<FormResponseSubmitArgs> = {
  id: FORM_RESPONSE_SUBMIT_ACTION_ID,
  label: 'Submit form response',
  description: 'Submits a filled response for the specified form.',
  risk: 'MEDIUM',
  domain: 'form',
  surfaces: ['ai', 'catalog'],
  argsSchema: formResponseSubmitArgsSchema,
  permission: (context) => context.canPerform('forms.submit'),
  run: async (args, context): Promise<ActionOutcome> => {
    context.showToast?.(`Submitted form${args.title ? ` · ${args.title}` : ''}`, 'success')
    return {
      kind: 'COMPLETED',
      result: { formId: args.formId, responseId: args.responseId },
      message: 'Submitted form',
      correlationId: context.correlationId!,
    }
  },
}

export const registerFormActions = (): void => {
  if (!getAction(FORM_FIELD_RENAME_ACTION_ID)) registerAction(formFieldRenameAction)
  if (!getAction(FORM_FIELD_CHANGE_TYPE_ACTION_ID)) registerAction(formFieldChangeTypeAction)
  if (!getAction(FORM_FIELD_TOGGLE_REQUIRED_ACTION_ID)) registerAction(formFieldToggleRequiredAction)
  if (!getAction(FORM_FIELD_DELETE_ACTION_ID)) registerAction(formFieldDeleteAction)
  if (!getAction(FORM_SECTION_RENAME_ACTION_ID)) registerAction(formSectionRenameAction)
  if (!getAction(FORM_SECTION_COLLAPSE_ACTION_ID)) registerAction(formSectionCollapseAction)
  if (!getAction(FORM_SECTION_DELETE_ACTION_ID)) registerAction(formSectionDeleteAction)
  if (!getAction(FORM_SUBMIT_RULES_ACTION_ID)) registerAction(formSubmitRulesAction)
  if (!getAction(FORM_SUBMIT_PREVIEW_ACTION_ID)) registerAction(formSubmitPreviewAction)
  if (!getAction(FORM_CANVAS_ADD_FIELD_ACTION_ID)) registerAction(formCanvasAddFieldAction)
  if (!getAction(FORM_CANVAS_ADD_SECTION_ACTION_ID)) registerAction(formCanvasAddSectionAction)
  if (!getAction(FORM_CANVAS_PREVIEW_ACTION_ID)) registerAction(formCanvasPreviewAction)
  if (!getAction(FORM_RESPONSE_SUBMIT_ACTION_ID)) registerAction(formResponseSubmitAction)
}

registerFormActions()
